import L from "leaflet";

/**
 * De warmtelaag van de herkomstkaart: waar veel dieren vandaan komen kleurt de kaart rood, waar weinig
 * vandaan komen groen.
 *
 * <p>Zelf getekend en geen leaflet.heat: het is weinig code, en een los pakket voor één laag is een
 * afhankelijkheid meer die bij elke Leaflet-update mee moet. Het werkt zoals zulke lagen altijd werken:
 * elke plek zet een zachte vlek in grijstinten op een doek (hoe meer dieren, hoe donkerder), overlappende
 * vlekken tellen op, en daarna krijgt elke grijstint een kleur uit het palet.
 */

export interface WarmtePunt {
  lat: number;
  lon: number;
  aantal: number;
}

/**
 * Het palet van weinig naar veel: blauw, via groen en geel naar rood.
 *
 * <p>Eerst begon het bij de merkkleur, een grijsgroen. Op een OpenStreetMap-kaart, die zelf groen en
 * beige is, verdween elke plek met een paar dieren daarin: je zag alleen Enschede. Blauw komt op die
 * ondergrond niet voor.
 */
export const WARMTE_STOPS: [number, string][] = [
  [0, "#2c7bb6"],
  [0.3, "#00a6ca"],
  [0.5, "#7fd08a"],
  [0.7, "#f9d057"],
  [0.85, "#f29e2e"],
  [1, "#d7191c"],
];

/**
 * Hoe zwaar een plek weegt, van 0 tot 1.
 *
 * <p>De wortel en niet lineair. Lineair is Enschede met driehonderd dieren een fel punt en is elk dorp
 * met vijf onzichtbaar — dan toont de kaart alleen waar de opvang staat, en dat wist je al. Met de wortel
 * blijft een dorp zichtbaar en blijft de stad het felst. Een ondergrens zorgt dat één dier ook een vlek is;
 * bij 0,12 was die vlek op de kaart niet terug te vinden, vandaar een kwart.
 */
export function gewicht(aantal: number, max: number): number {
  if (max <= 0 || aantal <= 0) return 0;
  return Math.max(0.25, Math.min(1, Math.sqrt(aantal / max)));
}

/**
 * De straal van een vlek in pixels.
 *
 * <p>Groeit mee met inzoomen: op het hele land liggen gemeenten dicht bij elkaar en moeten de vlekken
 * klein zijn, op een regio zouden diezelfde vlekken tot stipjes krimpen.
 */
export function straalBijZoom(zoom: number): number {
  return Math.round(Math.min(80, Math.max(22, 7 * (zoom - 3))));
}

/** Geeft elke grijstint op het doek een kleur uit het palet (256 kleuren, vier bytes elk). */
export function kleurIn(
  ctx: CanvasRenderingContext2D, breedte: number, hoogte: number, palet: Uint8ClampedArray,
): void {
  const beeld = ctx.getImageData(0, 0, breedte, hoogte);
  const d = beeld.data;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 0) continue;
    const j = a * 4;
    d[i] = palet[j];
    d[i + 1] = palet[j + 1];
    d[i + 2] = palet[j + 2];
    // Doorzichtig genoeg om de plaatsnamen eronder te lezen, maar ook een lichte vlek blijft zichtbaar;
    // de uiterste rand loopt vloeiend uit in plaats van met een harde lijn te stoppen.
    d[i + 3] = a < 8 ? a * 7 : Math.min(225, 50 + a * 0.7);
  }
  ctx.putImageData(beeld, 0, 0);
}

function maakKwast(straal: number): HTMLCanvasElement | null {
  const kwast = document.createElement("canvas");
  kwast.width = straal * 2;
  kwast.height = straal * 2;
  const ctx = kwast.getContext("2d");
  if (!ctx) return null;
  const verloop = ctx.createRadialGradient(straal, straal, 0, straal, straal, straal);
  verloop.addColorStop(0, "rgba(0,0,0,1)");
  verloop.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = verloop;
  ctx.fillRect(0, 0, straal * 2, straal * 2);
  return kwast;
}

function maakPalet(): Uint8ClampedArray | null {
  const doek = document.createElement("canvas");
  doek.width = 256;
  doek.height = 1;
  const ctx = doek.getContext("2d");
  if (!ctx) return null;
  const verloop = ctx.createLinearGradient(0, 0, 256, 0);
  for (const [stop, kleur] of WARMTE_STOPS) verloop.addColorStop(stop, kleur);
  ctx.fillStyle = verloop;
  ctx.fillRect(0, 0, 256, 1);
  return ctx.getImageData(0, 0, 256, 1).data;
}

/** De laag zelf: één doek over de kaart, opnieuw getekend na elke verschuiving of zoom. */
export class WarmteLaag extends L.Layer {
  private punten: WarmtePunt[];
  private kaart: L.Map | null = null;
  private doek: HTMLCanvasElement | null = null;
  private palet: Uint8ClampedArray | null = null;

  constructor(punten: WarmtePunt[]) {
    super();
    this.punten = punten;
  }

  onAdd(kaart: L.Map): this {
    this.kaart = kaart;
    const doek = document.createElement("canvas");
    // leaflet-zoom-hide: tijdens de zoomanimatie verbergen, anders schuift de oude vlek over de nieuwe kaart.
    doek.className = "fawkes-herkomst__warmte leaflet-zoom-hide";
    doek.style.position = "absolute";
    doek.style.pointerEvents = "none";
    kaart.getPanes().overlayPane.appendChild(doek);
    this.doek = doek;
    kaart.on("moveend zoomend resize", this.teken, this);
    this.teken();
    return this;
  }

  onRemove(kaart: L.Map): this {
    kaart.off("moveend zoomend resize", this.teken, this);
    this.doek?.remove();
    this.doek = null;
    this.kaart = null;
    return this;
  }

  private teken(): void {
    const kaart = this.kaart;
    const doek = this.doek;
    if (!kaart || !doek) return;
    const maat = kaart.getSize();
    doek.width = maat.x;
    doek.height = maat.y;
    L.DomUtil.setPosition(doek, kaart.containerPointToLayerPoint([0, 0]));
    const ctx = doek.getContext("2d");
    if (!ctx || this.punten.length === 0) return;
    ctx.clearRect(0, 0, maat.x, maat.y);
    const straal = straalBijZoom(kaart.getZoom());
    const kwast = maakKwast(straal);
    if (!kwast) return;
    const max = this.punten.reduce((m, p) => Math.max(m, p.aantal), 0);
    for (const p of this.punten) {
      const punt = kaart.latLngToContainerPoint([p.lat, p.lon]);
      if (punt.x < -straal || punt.y < -straal || punt.x > maat.x + straal || punt.y > maat.y + straal) {
        continue;
      }
      ctx.globalAlpha = gewicht(p.aantal, max);
      ctx.drawImage(kwast, punt.x - straal, punt.y - straal);
    }
    ctx.globalAlpha = 1;
    this.palet ??= maakPalet();
    if (this.palet) kleurIn(ctx, maat.x, maat.y, this.palet);
  }
}
